import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBooking, useBookingAction, useFolio } from '../api/queries';
import type { FolioEntry } from '../api/types';
import { BOOKING_STATUS, DEPARTMENT_LABEL, formatDate, formatDateTime, formatGhs } from '../format';
import { Badge, Button, ErrorNote, Icon, Loading, useReport, useToast } from '../ui/kit';
import { RefundDialog } from './Refund';
import { BookingMessages } from './Messages';
import { AssignRoomDialog, CheckOutDialog, ChargeDialog, PaymentDialog, ReasonDialog, VoidDialog } from './bookingDialogs';

type Dialog = null | 'charge' | 'pay' | 'assign' | 'checkout' | 'cancel';

/** What can go back on one payment: what was paid, less what was already refunded, and no more than the guest overpaid. */
function RefundFromFolio({ paymentId, folio, bookingId, onClose }: { paymentId: string; folio: NonNullable<ReturnType<typeof useFolio>['data']>; bookingId: string; onClose: () => void }) {
  const p = folio.payments.find((x) => x.id === paymentId);
  if (!p) return null;
  const refunded = folio.entries.filter((e) => e.type === 'REFUND' && e.paymentId === paymentId).reduce((s, e) => s + e.amount, 0);
  const max = Math.max(0, Math.min(p.amount - refunded, -folio.balance));
  return <RefundDialog title="Refund an overpayment" path={`/bookings/${bookingId}/payments/${paymentId}/refund`} paid={[{ method: p.method, amount: p.amount }]} partial={{ max }} onClose={onClose} />;
}

export function BookingDetail() {
  const { id = '' } = useParams();
  const { me, can } = useAuth();
  const today = me?.property.today ?? '';
  const booking = useBooking(id);
  const folio = useFolio(id);
  const act = useBookingAction(id);
  const toast = useToast();
  const report = useReport();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [voiding, setVoiding] = useState<FolioEntry | null>(null);
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const close = () => setDialog(null);

  if (booking.isLoading) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }
  if (booking.error || !booking.data) {
    return (
      <div className="page">
        <ErrorNote error={booking.error} retry={() => void booking.refetch()} />
      </div>
    );
  }

  const b = booking.data;
  const st = BOOKING_STATUS[b.status] ?? { label: b.status, tone: '' as const };
  const balance = folio.data?.balance ?? 0;
  const folioOpen = b.folio?.status === 'OPEN';
  const voided = new Set((folio.data?.entries ?? []).filter((e) => e.voidsEntryId).map((e) => e.voidsEntryId!));

  const run = (path: Parameters<typeof act.mutate>[0]['path'], body: Record<string, unknown> | undefined, done: string) =>
    act.mutate(
      { path, ...(body ? { body } : {}) },
      {
        onSuccess: () => {
          toast.ok(done);
          close();
        },
        onError: report,
      },
    );

  return (
    <div className="page">
      <Link to="/bookings" className="row small" style={{ textDecoration: 'none' }}>
        <span style={{ width: 18, height: 18 }}>
          <Icon name="back" />
        </span>
        All bookings
      </Link>

      <div className="page-head">
        <div>
          <div className="eyebrow num">{b.reference}</div>
          <h1 className="h1">{b.guest.fullName}</h1>
          <div className="row small muted">
            <Badge tone={st.tone}>{st.label}</Badge>
            {b.guest.phone ? <a href={`tel:${b.guest.phone}`}>{b.guest.phone}</a> : null}
          </div>
        </div>
      </div>

      {b.status === 'PENDING' && b.holdExpiresAt ? <div className="banner">Held until {formatDateTime(b.holdExpiresAt)}. Confirm it to keep the room.</div> : null}

      <div className="grid-2">
        <section className="card" aria-labelledby="stay">
          <div className="card-head">
            <h2 className="h2" id="stay">
              Stay
            </h2>
          </div>
          <dl className="kv">
            <dt>Arrive</dt>
            <dd>{formatDate(b.checkIn, true)}</dd>
            <dt>Depart</dt>
            <dd>{formatDate(b.checkOut, true)}</dd>
            <dt>Nights</dt>
            <dd className="num">{b.nights}</dd>
            <dt>Room</dt>
            <dd>{b.room ? `Room ${b.room.number} · ${b.roomType.name}` : `${b.roomType.name} (not assigned)`}</dd>
            <dt>Guests</dt>
            <dd>
              {b.adults} adult{b.adults === 1 ? '' : 's'}
              {b.children ? `, ${b.children} child${b.children === 1 ? '' : 'ren'}` : ''}
            </dd>
            <dt>Booked via</dt>
            <dd>{b.source.replace('_', ' ').toLowerCase()}</dd>
            <dt>Quoted total</dt>
            <dd className="num">{formatGhs(b.total)}</dd>
          </dl>
          {b.notes ? <p className="small muted" style={{ marginTop: 12 }}>{b.notes}</p> : null}
        </section>

        <section className="card" aria-labelledby="actions">
          <div className="card-head">
            <h2 className="h2" id="actions">
              Actions
            </h2>
          </div>
          <div className="stack">
            {b.status === 'PENDING' && can('booking.modify') ? (
              <Button variant="primary" block loading={act.isPending} onClick={() => run('confirm', undefined, 'Booking confirmed')}>
                Confirm booking
              </Button>
            ) : null}

            {(b.status === 'PENDING' || b.status === 'CONFIRMED') && can('booking.modify') && !b.room ? (
              <Button variant={b.status === 'CONFIRMED' ? 'primary' : ''} block onClick={() => setDialog('assign')}>
                Assign a room
              </Button>
            ) : null}

            {b.status === 'CONFIRMED' && b.room && can('booking.checkin') ? (
              <Button variant="primary" block loading={act.isPending} onClick={() => run('check-in', undefined, `Checked in to room ${b.room!.number}`)}>
                Check in
              </Button>
            ) : null}

            {b.status === 'CHECKED_IN' && can('folio.charge') ? (
              <Button block onClick={() => setDialog('charge')}>
                Add a charge
              </Button>
            ) : null}
            {(b.status === 'CHECKED_IN' || b.status === 'CONFIRMED' || b.status === 'PENDING') && folioOpen && (can('payment.record') || can('payment.initiate')) ? (
              <Button block onClick={() => setDialog('pay')}>
                Take a payment
              </Button>
            ) : null}
            {b.status === 'CHECKED_IN' && can('booking.checkout') ? (
              <Button variant="primary" block onClick={() => setDialog('checkout')}>
                Check out
              </Button>
            ) : null}

            {b.status === 'CONFIRMED' && can('booking.modify') && b.checkIn < today ? (
              <Button variant="danger" block loading={act.isPending} onClick={() => run('no-show', undefined, 'Marked as no-show')}>
                Mark as no-show
              </Button>
            ) : null}
            {(b.status === 'PENDING' || b.status === 'CONFIRMED') && can('booking.modify') ? (
              <Button variant="danger" block onClick={() => setDialog('cancel')}>
                Cancel booking
              </Button>
            ) : null}

            {can('folio.view') && (folio.data?.entries.length ?? 0) > 0 ? (
              <Link to={`/bookings/${id}/receipt`} className="btn block">
                {balance === 0 ? 'View receipt' : 'View invoice'}
              </Link>
            ) : null}

            {b.status === 'CHECKED_OUT' || b.status === 'CANCELLED' || b.status === 'NO_SHOW' ? <p className="muted small">This booking is finished. No further actions.</p> : null}
          </div>
        </section>
      </div>

      {can('message.view') ? <BookingMessages bookingId={b.id} /> : null}

      <section className="card" aria-labelledby="folio">
        <div className="card-head">
          <h2 className="h2" id="folio">
            Folio
          </h2>
          {b.folio ? <Badge tone={folioOpen ? 'info' : ''}>{folioOpen ? 'Open' : 'Closed'}</Badge> : null}
        </div>
        {folio.isLoading ? (
          <Loading rows={2} />
        ) : folio.error ? (
          <ErrorNote error={folio.error} retry={() => void folio.refetch()} />
        ) : (folio.data?.entries ?? []).length === 0 ? (
          <p className="muted small">Nothing charged yet. The room charge is added at check-in.</p>
        ) : (
          <table className="ledger">
            <thead>
              <tr>
                <th>Item</th>
                <th className="r">Amount</th>
              </tr>
            </thead>
            <tbody>
              {folio.data!.entries.map((e) => {
                const isVoided = voided.has(e.id);
                const credit = e.direction === 'CREDIT';
                return (
                  <tr key={e.id} className={isVoided ? 'voided' : ''}>
                    <td>
                      <div>{e.description}</div>
                      <div className="hint">
                        {e.type === 'PAYMENT' ? 'Payment' : DEPARTMENT_LABEL[e.department] ?? e.department} · {formatDateTime(e.createdAt)}
                        {e.reason ? ` · ${e.reason}` : ''}
                      </div>
                      {e.type === 'CHARGE' && !isVoided && folioOpen && can('folio.void') ? (
                        <button type="button" className="btn small ghost" style={{ marginLeft: -12 }} onClick={() => setVoiding(e)}>
                          Void
                        </button>
                      ) : null}
                      {e.type === 'PAYMENT' && e.paymentId && balance < 0 && can('refund.issue') ? (
                        <button type="button" className="btn small ghost" style={{ marginLeft: -12 }} onClick={() => setRefundingId(e.paymentId!)}>
                          Refund
                        </button>
                      ) : null}
                    </td>
                    <td className="r num">{credit ? `−${formatGhs(e.amount)}` : formatGhs(e.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {folio.data ? (
          <div className="total-row">
            <span>{balance > 0 ? 'Balance due' : balance < 0 ? 'Overpaid' : 'Settled'}</span>
            <span className={`amount num ${balance > 0 ? 'owing' : 'settled'}`}>{formatGhs(Math.abs(balance))}</span>
          </div>
        ) : null}
      </section>

      <ChargeDialog open={dialog === 'charge'} onClose={close} bookingId={id} />
      <PaymentDialog open={dialog === 'pay'} onClose={close} bookingId={id} balance={balance} />
      {refundingId && folio.data ? <RefundFromFolio paymentId={refundingId} folio={folio.data} bookingId={id} onClose={() => setRefundingId(null)} /> : null}
      <VoidDialog open={!!voiding} onClose={() => setVoiding(null)} bookingId={id} entry={voiding} />
      <AssignRoomDialog open={dialog === 'assign'} onClose={close} booking={b} pending={act.isPending} onAssign={(roomId) => run('assign-room', roomId ? { roomId } : undefined, 'Room assigned')} />
      <CheckOutDialog open={dialog === 'checkout'} onClose={close} balance={balance} canOverride={can('folio.settle_override')} pending={act.isPending} onConfirm={(override) => run('check-out', override ? { settlementOverride: true } : undefined, 'Checked out. The room is now waiting to be cleaned.')} />
      <ReasonDialog open={dialog === 'cancel'} onClose={close} title="Cancel this booking?" label="Reason for cancelling" confirmLabel="Cancel booking" pending={act.isPending} onSubmit={(reason) => run('cancel', { reason }, 'Booking cancelled')} />
    </div>
  );
}
